import type { Lesson } from '../types';
import { resolveCampSessionDatesForCapacity } from './campEnrollment';

export type LessonBillingPriceInput = Pick<
  Lesson,
  | 'lessonType'
  | 'termId'
  | 'price'
  | 'pricePerSession'
  | 'numberOfSessions'
  | 'pricingType'
  | 'sessionPrice'
  | 'startDate'
  | 'campDayPrice'
  | 'campFullPrice'
>;

export type CampBillingContext = {
  campBookingType?: 'daily' | 'full_camp';
};

/** Shown when bill-by-attendance is enabled but a term lesson has no per-session price. */
export const BILLING_MISSING_SESSION_PRICE_MESSAGE =
  'Set a per-session price on this lesson to bill by attendance. Edit the lesson in Term Lessons and add a per-session price.';

/**
 * Term-bound group/private lessons require a positive explicit `sessionPrice` for bill-by-attendance.
 */
export function isTermLessonMissingBillingSessionPrice(
  lesson: Pick<Lesson, 'lessonType' | 'termId' | 'sessionPrice'>
): boolean {
  if (!lesson.termId) return false;
  if (lesson.lessonType !== 'group' && lesson.lessonType !== 'private') return false;
  return lesson.sessionPrice == null || lesson.sessionPrice <= 0;
}

function hasPositiveExplicitSessionPrice(sessionPrice: number | null | undefined): boolean {
  return sessionPrice != null && sessionPrice > 0;
}

/**
 * List prices for the student billing report (per session vs package total).
 * Term group/private per-session list price is only the explicit `sessionPrice` from the lesson form.
 * Standalone private lessons store per-session rate in `pricePerSession` and package total in `price`.
 */
export function resolveLessonBillingPrices(
  lesson: LessonBillingPriceInput,
  sessionsTotal: number
): { perSessionPrice: number; fullPrice: number } {
  const attendanceSessionCount =
    sessionsTotal > 0 ? sessionsTotal : Math.max(lesson.numberOfSessions ?? 0, 1);
  const configuredSessionCount = Math.max(lesson.numberOfSessions ?? 0, 1);

  if (lesson.lessonType === 'group') {
    const fullPrice = lesson.price ?? 0;
    if (hasPositiveExplicitSessionPrice(lesson.sessionPrice)) {
      return { perSessionPrice: lesson.sessionPrice!, fullPrice };
    }
    return { perSessionPrice: 0, fullPrice };
  }

  if (lesson.lessonType === 'private' && lesson.termId) {
    const fullPrice = lesson.price ?? 0;
    const perSessionPrice =
      lesson.sessionPrice != null && lesson.sessionPrice > 0 ? lesson.sessionPrice : 0;
    return { perSessionPrice, fullPrice };
  }

  if (lesson.lessonType === 'private') {
    if (lesson.pricePerSession != null) {
      const perSessionPrice = lesson.pricePerSession;
      const fullPrice = lesson.price ?? perSessionPrice * configuredSessionCount;
      return { perSessionPrice, fullPrice };
    }
    if (lesson.pricingType === 'per_session') {
      const perSessionPrice = lesson.price ?? 0;
      return {
        perSessionPrice,
        fullPrice: perSessionPrice * configuredSessionCount,
      };
    }
    const fullPrice = lesson.price ?? 0;
    const perSessionPrice =
      configuredSessionCount > 0 ? fullPrice / configuredSessionCount : fullPrice;
    return { perSessionPrice, fullPrice };
  }

  if (lesson.lessonType === 'camp') {
    return {
      perSessionPrice: lesson.campDayPrice ?? 0,
      fullPrice: lesson.campFullPrice ?? lesson.price ?? 0,
    };
  }

  const fullPrice = lesson.price ?? 0;
  return { perSessionPrice: 0, fullPrice };
}

export type BillingTotalDueInput = LessonBillingPriceInput & {
  termId?: string | null;
};

export type BillingBasis =
  | 'enrollment_total'
  | 'attendance_in_period'
  | 'term_full_price_due_in_period'
  | 'term_full_price_outside_period'
  | 'missing_session_price';

export type BillingPerSessionDisplayParams = {
  termId?: string | null;
  billingBasis?: BillingBasis;
  lessonType?: Lesson['lessonType'];
  sessionPrice?: number | null;
  pricePerSession?: number | null;
  pricingType?: 'total' | 'per_session';
  perSessionPrice?: number;
  campDayPrice?: number | null;
};

/** Per-session list price is only shown when explicitly configured on the lesson, never derived. */
export function shouldShowBillingPerSessionPrice(params: BillingPerSessionDisplayParams): boolean {
  if (params.billingBasis === 'missing_session_price') return false;

  if (params.lessonType === 'camp') {
    return (params.campDayPrice ?? params.perSessionPrice ?? 0) > 0;
  }

  if (
    params.billingBasis === 'term_full_price_due_in_period' ||
    params.billingBasis === 'term_full_price_outside_period'
  ) {
    return false;
  }

  if (params.lessonType === 'group' || (params.lessonType === 'private' && params.termId)) {
    return hasPositiveExplicitSessionPrice(params.sessionPrice);
  }

  if (params.lessonType === 'private') {
    if (params.pricePerSession != null && params.pricePerSession > 0) return true;
    return params.pricingType === 'per_session' && (params.perSessionPrice ?? 0) > 0;
  }

  return false;
}

function filterCampDatesToPeriod(
  dates: string[],
  periodStart?: string,
  periodEnd?: string
): string[] {
  return dates.filter((ymd) => {
    if (periodStart && ymd < periodStart) return false;
    if (periodEnd && ymd > periodEnd) return false;
    return true;
  });
}

/**
 * Camp session denominator for billing report (attended / total days).
 * Uses materialized or computed camp week dates, not attendance row count.
 */
function normalizeCampYmdDates(dates: string[]): string[] {
  return [
    ...new Set(
      dates
        .map((d) => String(d).slice(0, 10))
        .filter((d) => /^\d{4}-\d{2}-\d{2}$/.test(d))
    ),
  ];
}

export function resolveCampBillingSessionsTotal(params: {
  lesson: Omit<LessonBillingPriceInput, 'startDate'> & {
    startDate?: unknown;
    daysOfWeek?: number[];
    campSessionDates?: string[];
  };
  campBookingType?: 'daily' | 'full_camp';
  campBookedDays?: string[];
  /** Fallback for daily bookings when `campBookedDays` is absent (e.g. legacy rows). */
  attendanceDates?: string[];
  periodStart?: string;
  periodEnd?: string;
}): number {
  const { lesson, campBookingType, campBookedDays, attendanceDates, periodStart, periodEnd } =
    params;

  if (campBookingType === 'daily') {
    if (campBookedDays?.length) {
      const booked = normalizeCampYmdDates(campBookedDays);
      return filterCampDatesToPeriod(booked, periodStart, periodEnd).length;
    }
    if (attendanceDates?.length) {
      const fromAttendance = normalizeCampYmdDates(attendanceDates);
      return filterCampDatesToPeriod(fromAttendance, periodStart, periodEnd).length;
    }
    return 0;
  }

  const campDates = resolveCampSessionDatesForCapacity(lesson);
  return filterCampDatesToPeriod(campDates, periodStart, periodEnd).length;
}

function isAttendancePricedLesson(
  lesson: BillingTotalDueInput,
  options?: CampBillingContext
): boolean {
  if (lesson.lessonType === 'camp') {
    return options?.campBookingType !== 'full_camp';
  }
  if (lesson.lessonType === 'group' && hasPositiveExplicitSessionPrice(lesson.sessionPrice)) return true;
  if (lesson.pricingType === 'per_session') return true;
  if (lesson.lessonType === 'private' && !lesson.termId && lesson.pricingType !== 'total') return true;
  return false;
}

/**
 * Amount owed for manual payment reconciliation (list prices × attendance).
 * Ignores stored booking totals, which may reflect legacy split shares or discounts.
 */
export function resolveBillingTotalDue(
  lesson: BillingTotalDueInput,
  params: {
    perSessionPrice: number;
    fullPrice: number;
    sessionsAttended: number;
    campBookingType?: 'daily' | 'full_camp';
  }
): number {
  const { perSessionPrice, fullPrice, sessionsAttended, campBookingType } = params;

  if (lesson.lessonType === 'camp') {
    if (campBookingType === 'full_camp') return fullPrice;
    return perSessionPrice * sessionsAttended;
  }
  if (lesson.lessonType === 'group' && hasPositiveExplicitSessionPrice(lesson.sessionPrice)) {
    return perSessionPrice * sessionsAttended;
  }
  if (lesson.pricingType === 'per_session') {
    return perSessionPrice * sessionsAttended;
  }
  if (lesson.lessonType === 'private' && !lesson.termId && lesson.pricingType !== 'total') {
    return perSessionPrice * sessionsAttended;
  }
  return fullPrice;
}

export function resolveBillingPeriodTotalDue(
  lesson: BillingTotalDueInput,
  params: {
    perSessionPrice: number;
    fullPrice: number;
    sessionsAttended: number;
    hasPeriod: boolean;
    chargeStartsInPeriod?: boolean;
    campBookingType?: 'daily' | 'full_camp';
    /** When true, period due is always list per-session/day price × attended sessions in the period. */
    billByAttendance?: boolean;
  }
): { periodTotalDue: number | null; billingBasis: BillingBasis } {
  const {
    perSessionPrice,
    fullPrice,
    sessionsAttended,
    hasPeriod,
    chargeStartsInPeriod,
    campBookingType,
    billByAttendance,
  } = params;

  if (!hasPeriod) {
    return {
      periodTotalDue: resolveBillingTotalDue(lesson, {
        perSessionPrice,
        fullPrice,
        sessionsAttended,
        campBookingType,
      }),
      billingBasis: 'enrollment_total',
    };
  }

  if (billByAttendance) {
    if (isTermLessonMissingBillingSessionPrice(lesson)) {
      return {
        periodTotalDue: null,
        billingBasis: 'missing_session_price',
      };
    }
    return {
      periodTotalDue: perSessionPrice * sessionsAttended,
      billingBasis: 'attendance_in_period',
    };
  }

  if (isAttendancePricedLesson(lesson, { campBookingType })) {
    return {
      periodTotalDue: perSessionPrice * sessionsAttended,
      billingBasis: 'attendance_in_period',
    };
  }

  return {
    periodTotalDue: chargeStartsInPeriod ? fullPrice : 0,
    billingBasis: chargeStartsInPeriod
      ? 'term_full_price_due_in_period'
      : 'term_full_price_outside_period',
  };
}
